/** Safety cap so launch continuity never deadlocks the prompt drainer. */
export const LAUNCH_HANDOFF_TIMEOUT_MS = 3_000;

export type LaunchHandoffRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** How continuity is delivered; prefer not to branch on this outside the launch module. */
export type LaunchHandoffMode = "motion" | "none";

export type LaunchHandoffSurrogatePreview = {
  previewText: string;
  fileNames: string[];
  contextLabels: string[];
  uploading: boolean;
  uploadProgress: Record<string, number>;
};

export const emptyLaunchSurrogatePreview = (): LaunchHandoffSurrogatePreview => ({
  previewText: "",
  fileNames: [],
  contextLabels: [],
  uploading: false,
  uploadProgress: {},
});

/** Minimal router surface for launch navigation. */
export type LaunchRouter = {
  push: (href: string) => void;
};

export function rectFromElement(el: Element | null): LaunchHandoffRect | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

export function isValidLaunchHandoffRect(rect: LaunchHandoffRect | null): rect is LaunchHandoffRect {
  return !!rect && rect.width > 0 && rect.height > 0;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Picks continuity mode for Overview → workbench navigation.
 * Flight overlay on motion for lightweight text-only launches; instant path
 * when reduced motion is preferred or when files/context add visual weight.
 */
export function resolveLaunchHandoffMode(opts?: {
  fileCount?: number;
  contextCount?: number;
}): LaunchHandoffMode {
  if (prefersReducedMotion()) return "none";
  if ((opts?.fileCount ?? 0) > 0) return "none";
  if ((opts?.contextCount ?? 0) > 0) return "none";
  return "motion";
}
